import {
  Box,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { ToggleSwitch } from '@retail/components';
import {
  AssortmentConfig,
  availableCharsets,
  availableTotalfileFrequencies,
} from '@retail/member-config/types';
import { InputWithStepper } from '@retail/components';
import { Dispatch, SetStateAction } from 'react';

interface Props {
  updatedRows: AssortmentConfig[];
  setUpdatedRows: Dispatch<SetStateAction<AssortmentConfig[]>>;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 160,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

export function AssortmentsFieldsUpateTable({ updatedRows, setUpdatedRows }: Props) {
  const t = useAppTFunction();

  const handleCellChange = (
    rowId: string | number,
    field: keyof AssortmentConfig,
    newValue: any
  ) => {
    setUpdatedRows((prevRows) =>
      prevRows.map((row) => (row.id === rowId ? { ...row, [field]: newValue } : row))
    );
  };

  const resetCellsOnSftpSwichOff = (rowId: string | number) => {
    setUpdatedRows((prevRows) =>
      prevRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              erpId: undefined,
              erpType: undefined,
              totalfilFormat: undefined,
              totalfilCharset: undefined,
              totalfilVilmaVersion: undefined,
              totalfilNumberOfFutureDays: undefined,
              totalfilSftpDirectory: undefined,
              totalfilSftpFrequency: undefined,
            }
          : row
      )
    );
  };

  const columns: GridColDef[] = [
    {
      ...baseColumnProps,
      field: 'assortmentName',
      width: 260,
      headerName: t('myAssortment.memberConfig.assortmentName').toUpperCase(),
      renderCell: ({ row }) => (
        <TextField
          sx={{
            '& .MuiInputBase-root': {
              height: 28,
              minWidth: 244,
            },
          }}
          size="small"
          value={row.title}
          onKeyDown={(e) => e.stopPropagation()}
          onChange={(e) => handleCellChange(row.id, 'title', e.target.value)}
        />
      ),
    },
    {
      ...baseColumnProps,
      field: 'salesPriceNotification',
      width: 105,
      renderHeader: () => (
        <Tooltip title={t('myAssortment.memberConfig.publishPriceDesc')}>
          <Typography variant="subtitle1" whiteSpace="wrap">
            {t('myAssortment.memberConfig.publishPrice').toUpperCase()}
          </Typography>
        </Tooltip>
      ),
      renderCell: ({ row }) => (
        <ToggleSwitch
          text=""
          toggle={row.sendSalesPriceUpdated ?? false}
          setToggle={() =>
            handleCellChange(row.id, 'sendSalesPriceUpdated', !row.sendSalesPriceUpdated)
          }
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilToSftp',
      width: 85,
      headerName: 'SFTP',
      renderCell: ({ row }) => (
        <ToggleSwitch
          text=""
          toggle={row.totalfilToSftp ?? false}
          setToggle={() => {
            if (row.totalfilToSftp) {
              resetCellsOnSftpSwichOff(row.id);
              handleCellChange(row.id, 'totalfilToSftp', !row.totalfilToSftp);
            } else {
              handleCellChange(row.id, 'totalfilToSftp', !row.totalfilToSftp);
            }
          }}
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'erpId',
      headerName: 'ERP-ID',
      width: 100,
      renderCell: ({ row }) => (
        <TextField
          sx={{
            '& .MuiInputBase-root': {
              height: 28,
            },
          }}
          disabled={!row.totalfilToSftp}
          size="small"
          value={row.erpId ?? ''}
          onChange={(e) => handleCellChange(row.id, 'erpId', e.target.value)}
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'erpType',
      headerName: t('myAssortment.memberConfig.erpType'),
      width: 100,
      renderCell: ({ row }) => (
        <TextField
          sx={{
            '& .MuiInputBase-root': {
              height: 28,
            },
          }}
          disabled={!row.totalfilToSftp}
          size="small"
          value={row.erpType ?? ''}
          onChange={(e) => handleCellChange(row.id, 'erpType', e.target.value)}
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfileFileFormat',
      headerName: t('myAssortment.memberConfig.fileFormat').toUpperCase(),
      renderCell: ({ row }) => (
        <ToggleButtonGroup
          size="small"
          value={row.totalfilFormat}
          sx={{ height: 28 }}
          exclusive
          disabled={!row.totalfilToSftp}
          onChange={(_, val: 'VILMA' | 'CSV') => handleCellChange(row.id, 'totalfilFormat', val)}
        >
          <ToggleButton size="small" value="VILMA" sx={{ p: '4px !important' }}>
            VILMA
          </ToggleButton>
          <ToggleButton size="small" value="CSV" sx={{ p: '4px !important' }}>
            CSV
          </ToggleButton>
        </ToggleButtonGroup>
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfileCharset',
      headerName: t('myAssortment.memberConfig.charset').toUpperCase(),
      renderCell: ({ row }) => (
        <Select
          size="small"
          value={row.totalfilCharset ?? ''}
          sx={{ height: 32, minWidth: 120 }}
          disabled={!row.totalfilToSftp}
          onChange={(e) =>
            handleCellChange(
              row.id,
              'totalfilCharset',
              e.target.value === '' ? undefined : e.target.value
            )
          }
        >
          {availableCharsets.map((charset) => (
            <MenuItem key={charset} value={charset}>
              {charset}
            </MenuItem>
          ))}
          <MenuItem value={''}>{t('myAssortment.memberConfig.reset')}</MenuItem>
        </Select>
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfileVilmaVersion',
      headerName: t('myAssortment.memberConfig.vilmaVersion').toUpperCase(),
      renderCell: ({ row }) => (
        <ToggleButtonGroup
          size="small"
          value={row.totalfilVilmaVersion}
          exclusive
          sx={{ height: 28 }}
          disabled={!row.totalfilToSftp}
          onChange={(_, val: 'VILMA_27' | 'VILMA_28') =>
            handleCellChange(row.id, 'totalfilVilmaVersion', val)
          }
        >
          <ToggleButton size="small" value="VILMA_27" sx={{ p: '4px !important' }}>
            {t('myAssortment.memberConfig.vilma_27')}
          </ToggleButton>
          <ToggleButton size="small" value="VILMA_28" sx={{ p: '4px !important' }}>
            {t('myAssortment.memberConfig.vilma_28')}
          </ToggleButton>
        </ToggleButtonGroup>
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilFutureDays',
      headerName: t('myAssortment.memberConfig.futureDays').toUpperCase(),
      renderCell: ({ row }) => (
        <InputWithStepper
          disabled={!row.totalfilToSftp}
          inputValue={row.totalfilNumberOfFutureDays?.toString() ?? ''}
          fieldWidth="small"
          setInputValue={(value?: string) =>
            handleCellChange(row.id, 'totalfilNumberOfFutureDays', value)
          }
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilSftpDirectory',
      headerName: t('myAssortment.memberConfig.sftpDirectory').toUpperCase(),
      renderCell: ({ row }) => (
        <TextField
          sx={{
            '& .MuiInputBase-root': {
              height: 28,
              maxWidth: 100,
            },
          }}
          disabled={!row.totalfilToSftp}
          size="small"
          value={row.totalfilSftpDirectory ?? ''}
          onChange={(e) => handleCellChange(row.id, 'totalfilSftpDirectory', e.target.value)}
        />
      ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilSftpFrequency',
      headerName: t('myAssortment.memberConfig.sftpFrequency').toUpperCase(),
      renderCell: ({ row }) => (
        <Select
          size="small"
          value={row.totalfilSftpFrequency ?? ''}
          sx={{ height: 32, minWidth: 110 }}
          disabled={!row.totalfilToSftp}
          onChange={(e) =>
            handleCellChange(
              row.id,
              'totalfilSftpFrequency',
              e.target.value === '' ? undefined : e.target.value
            )
          }
        >
          {availableTotalfileFrequencies.map((freq) => (
            <MenuItem key={freq} value={freq}>
              {freq}
            </MenuItem>
          ))}
          <MenuItem value={''}>{t('myAssortment.memberConfig.reset')}</MenuItem>
        </Select>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <DataGridPremium
          rows={updatedRows}
          columns={columns}
          hideFooter
          disableColumnMenu
          rowHeight={80}
          columnHeaderHeight={80}
        />
      </Box>
    </Box>
  );
}
